/**
 * Cluster outreach (Nautica, 18 Sep 2026): pick the nearby proven talent for a cluster, write one
 * message, send it to each person individually, and log every recipient as a lead on every shoot in
 * the message so the outreach section fills itself in. Her format, verbatim:
 *
 *   Hey NAME! Are you free for any of the following shoots?
 *   DATE, LOCATION, # OF HOURS, for a ROLE
 *   DATE, LOCATION, # OF HOURS, for a ROLE
 *
 * 22 Sep 2026 (Nautica): the date carries its year and each line says which role the shoot still needs, from the
 * event's open slots (photographer / second photographer / videographer).
 *
 * or, for one shoot, "Are you free for the following shoot?". Pure functions; the route and the page
 * share them.
 */

import { RECIPIENT_TOKEN, longMonthDay, signatureBlock, roleWords } from './outreachTemplates.js'

/** "for a photographer or a videographer": the roles a shoot still needs, from its open-slot flags. */
export function rolesNeededText(shoot = {}) {
  const words = []
  if (shoot.needs_photo) words.push(roleWords('photographer'))
  if (shoot.needs_second) words.push(roleWords('second_photographer'))
  if (shoot.needs_video) words.push(roleWords('videographer'))
  if (!words.length) return ''
  return 'for a ' + words.join(' or a ')
}

/** "September 19, 2026, Auburn, WA, 8 hours, for a second photographer" */
export function shootLine(shoot = {}) {
  const parts = [longMonthDay(shoot.event_date) || 'date TBD', shoot.city || 'location TBD']
  const hours = Number(shoot.hours)
  if (Number.isFinite(hours) && hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`)
  const roles = rolesNeededText(shoot)
  if (roles) parts.push(roles)
  return parts.join(', ')
}

/**
 * The message for one recipient (or with the {first_name} token for many). `shoots` are the cluster's
 * events the coordinator kept ticked, in date order.
 */
export function renderClusterMessage({ recipientName = RECIPIENT_TOKEN, shoots = [], senderName, senderTitle, company } = {}) {
  const name = String(recipientName || '').trim()
  const greet = name ? `Hey ${name}!` : 'Hey!'
  const list = [...(shoots || [])].sort((a, b) => String(a.event_date || '').localeCompare(String(b.event_date || '')))
  const question = list.length === 1 ? 'Are you free for the following shoot?' : 'Are you free for any of the following shoots?'
  const lines = list.map(shootLine).join('\n')
  return `${greet} ${question}\n\n${lines}\n\nLet me know either way,\n${signatureBlock(senderName, senderTitle, company)}`
}

/**
 * Which lead rows a recipient gets on one shoot. A videographer is a lead for the video slot; a
 * photographer for the photo slot and the second-shooter slot. Nothing for a slot the shoot does not
 * need, and nothing for a person who cannot fill it.
 */
export function leadRolesFor(needs = {}, isVideographer = false) {
  const roles = []
  if (isVideographer) {
    if (needs.needs_video) roles.push('videographer')
  } else {
    if (needs.needs_photo) roles.push('photographer')
    if (needs.needs_second) roles.push('second_photographer')
  }
  return roles
}

export const CLUSTER_MAX_RECIPIENTS = 50
export const CLUSTER_MAX_SHOOTS = 20

/** Validate a cluster send before anything is written. */
export function validateClusterSend({ talentIds = [], eventIds = [], text = '' } = {}) {
  if (!talentIds.length) return { ok: false, message: 'Pick at least one person.' }
  if (talentIds.length > CLUSTER_MAX_RECIPIENTS) return { ok: false, message: `At most ${CLUSTER_MAX_RECIPIENTS} people at a time.` }
  if (!eventIds.length) return { ok: false, message: 'Keep at least one shoot in the message.' }
  if (eventIds.length > CLUSTER_MAX_SHOOTS) return { ok: false, message: `At most ${CLUSTER_MAX_SHOOTS} shoots in one message.` }
  const t = String(text || '').trim()
  if (!t) return { ok: false, message: 'The message is empty.' }
  if (t.length > 4000) return { ok: false, message: 'The message is longer than 4000 characters.' }
  return { ok: true, text: t }
}

/**
 * What each person has already been asked about, from the outreach log (Trera, 18 Sep 2026: a photographer
 * who lives in the cluster would otherwise be hit with the same message on repeat). Returns, per talent id,
 * the shoots they were contacted about and when, newest first. The coordinator sees it on the row and beside
 * each shoot toggle; sending stays her call.
 */
export function askedByTalent(leads = [], eventIds = []) {
  const wanted = new Set(eventIds)
  const out = new Map()
  for (const l of leads || []) {
    if (!l?.talent_id || !wanted.has(l.booking_id) || !l.contacted_at) continue
    const list = out.get(l.talent_id) || []
    const cur = list.find(x => x.booking_id === l.booking_id)
    if (!cur) list.push({ booking_id: l.booking_id, at: l.contacted_at })
    else if (String(l.contacted_at) > String(cur.at)) cur.at = l.contacted_at
    out.set(l.talent_id, list)
  }
  for (const list of out.values()) list.sort((a, b) => String(b.at).localeCompare(String(a.at)))
  return out
}

/** "Asked Sep 18 about Oct 3, Oct 24" for one talent row; null when never asked about these shoots. */
export function describeAsked(asked = [], eventsById = new Map(), fmt = (d) => String(d).slice(0, 10)) {
  if (!asked.length) return null
  const latest = asked[0].at
  const shoots = asked.map(a => eventsById.get(a.booking_id)?.event_date).filter(Boolean).sort().map(fmt)
  return `Asked ${fmt(latest)}${shoots.length ? ` about ${shoots.join(', ')}` : ''}`
}
